import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaPlus, FaTrash, FaEdit, FaSearch } from "react-icons/fa";
import { useAuth } from '../../context/AuthContext';
import axios from "axios";
import { toast } from "react-toastify";

const Sheet = ({ isOpen, onClose, children }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed top-0 right-0 w-full max-w-md h-full bg-white shadow-xl z-50 flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-xl font-semibold">Vendedor</h3>
              <button onClick={onClose} className="text-gray-600 hover:text-gray-900 text-2xl font-bold">
                &times;
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-grow">{children}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const CompVendedores = () => {
  const { token } = useAuth();
  const [vendedorEdit, setVendedorEdit] = useState(null);
  const [vendedores, setVendedores] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Traer vendedores al montar
  useEffect(() => {
    const fetchVendedores = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL}/user`,
          {
            headers: {
              "x-auth-token": token,
            },
          }
        );
        setVendedores(response.data.users);
      } catch (err) {
        setError("Error al cargar vendedores.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchVendedores();
    }
  }, [token]);

  // Filtrar vendedores por nombre o apellido
  const filteredVendedores = useMemo(() => {
    return vendedores.filter((u) =>
      `${u.nombre_user} ${u.apellido_user}`.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [vendedores, searchTerm]);

  const openNewVendedor = () => {
    setVendedorEdit(null);
    setIsSheetOpen(true);
  };

  const openEditVendedor = (vendedor) => {
    setVendedorEdit(vendedor);
    setIsSheetOpen(true);
  };

  const closeSheet = () => {
    setIsSheetOpen(false);
  };

  // Guardar o actualizar vendedor con backend
  const handleSaveVendedor = async (user) => {
    setLoading(true);
    setError("");
    try {
      if (user.id_user) {
        // Editar (PUT)
        const response = await axios.put(
          `${import.meta.env.VITE_API_URL}/user/${user.id_user}`,
          user,
          {
            headers: {
              "x-auth-token": token,
            },
          }
        );
        setVendedores((prev) =>
          prev.map((u) => (u.id_user === user.id_user ? response.data : u))
        );
      } else {
        // Nuevo (POST)
        const response = await axios.post(
          `${import.meta.env.VITE_API_URL}/user`,
          user,
          {
            headers: {
              "x-auth-token": token,
            },
          }
        );
        setVendedores((prev) => [response.data, ...prev]);
      }
      closeSheet();
    } catch (err) {
      setError("Error al guardar vendedor.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Eliminar vendedor con backend
  const handleDeleteVendedor = async (id_user) => {
    if (window.confirm("¿Querés eliminar este vendedor?")) {
      setLoading(true);
      setError("");
      try {
        await axios.delete(`${import.meta.env.VITE_API_URL}/user/${id_user}`, {
          headers: {
            "x-auth-token": token,
          },
        });
        setVendedores((prev) => prev.filter((u) => u.id_user !== id_user));
      } catch (err) {
        setError("Error al eliminar vendedor.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="max-w-5xl mt-12 mx-auto p-6">
      <h2 className="text-4xl font-bold mb-6 text-center text-gray-700">Gestión de Vendedores</h2>

      {/* Buscador y botón agregar */}
      <div className="flex mb-6 gap-4">
        <div className="flex items-center border border-gray-300 rounded px-3 py-2 flex-grow">
          <FaSearch className="text-gray-500 mr-2" />
          <input
            type="text"
            placeholder="Buscar vendedor por nombre o apellido..."
            className="w-full focus:outline-none"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button
          onClick={openNewVendedor}
          className="bg-purple-600 hover:bg-purple-700 text-white px-6 rounded shadow flex items-center gap-2"
        >
          <FaPlus />
          Agregar Vendedor
        </button>
      </div>

      {/* Lista de vendedores */}
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-gray-100 text-left">
            <th className="p-3 border">Nombre</th>
            <th className="p-3 border">Apellido</th>
            <th className="p-3 border">Email</th>
            <th className="p-3 border">Rol</th>
            <th className="p-3 border text-center">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {filteredVendedores.length === 0 && (
            <tr>
              <td colSpan={5} className="p-4 text-center text-gray-500">
                No se encontraron vendedores.
              </td>
            </tr>
          )}
          {filteredVendedores.map((user) => (
            <tr key={user.id_user} className="border-b hover:bg-gray-50">
              <td className="p-3">{user.nombre_user}</td>
              <td className="p-3">{user.apellido_user}</td>
              <td className="p-3">{user.email_user}</td>
              <td className="p-3">{user.role}</td>
              <td className="p-3 flex gap-2 justify-center">
                <button
                  onClick={() => openEditVendedor(user)}
                  className="text-blue-600 hover:text-blue-800"
                  title="Editar"
                >
                  <FaEdit />
                </button>
                <button
                  onClick={() => handleDeleteVendedor(user.id_user)}
                  className="text-red-600 hover:text-red-800"
                  title="Eliminar"
                >
                  <FaTrash />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {isSheetOpen && (
        <Sheet isOpen={isSheetOpen} onClose={closeSheet}>
          <VendedorForm
            vendedor={vendedorEdit}
            onSave={handleSaveVendedor}
            onCancel={closeSheet}
          />
        </Sheet>
      )}
    </div>
  );
};

const VendedorForm = ({ vendedor, onSave, onCancel }) => {
  const [nombre_user, setNombreUser] = useState(vendedor?.nombre_user || "");
  const [apellido_user, setApellidoUser] = useState(vendedor?.apellido_user || "");
  const [email_user, setEmailUser] = useState(vendedor?.email_user || "");
  const [password_user, setPasswordUser] = useState("");
  const [role, setRole] = useState(vendedor?.role || "");

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!nombre_user.trim()) {
      alert("El nombre es obligatorio");
      return;
    }
    if (!apellido_user.trim()) {
      alert("El apellido es obligatorio");
      return;
    }
    if (!email_user.trim()) {
      alert("El email es obligatorio");
      return;
    }
    if (!role.trim()) {
      alert("El rol es obligatorio");
      return;
    }

    onSave({
      id_user: vendedor?.id_user,
      nombre_user: nombre_user.trim(),
      apellido_user: apellido_user.trim(),
      email_user: email_user.trim(),
      password_user: password_user.trim() || undefined, // Solo enviar si se escribe
      role: role.trim(),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block font-semibold mb-1">Nombre</label>
        <input
          type="text"
          value={nombre_user}
          onChange={(e) => setNombreUser(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
          autoFocus
        />
      </div>
      <div>
        <label className="block font-semibold mb-1">Apellido</label>
        <input
          type="text"
          value={apellido_user}
          onChange={(e) => setApellidoUser(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>
      <div>
        <label className="block font-semibold mb-1">Email</label>
        <input
          type="email"
          value={email_user}
          onChange={(e) => setEmailUser(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>
      <div>
        <label className="block font-semibold mb-1">Contraseña {vendedor ? "(dejar vacío para no cambiar)" : ""}</label>
        <input
          type="password"
          value={password_user}
          onChange={(e) => setPasswordUser(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>
      <div>
        <label className="block font-semibold mb-1">Rol</label>
        <input
          type="text"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>
      <div className="flex justify-end space-x-4 mt-6">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-100"
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded"
        >
          Guardar
        </button>
      </div>
    </form>
  );
};

export default CompVendedores;
